import {
  Alert,
  Button,
  Center,
  Group,
  Loader,
  Paper,
  Stack,
  Text,
  Title,
  useComputedColorScheme,
} from '@mantine/core';
import { useTranslation } from '@refinedev/core';
import type { ExternalPaymentLinkSummary } from '@billing/validation';
import { IconExternalLink } from '@tabler/icons-react';
import { DebitTimeline } from './DebitTimeline';
import { useGetDebitByClaim } from 'api/resource/debit';
import { useClaimExternalPaymentLinks } from 'api/resource/claimExternalPayment';
import { useCurrentSiteStore } from 'store';
import { getOutlineControlProps } from 'utils/buttonUtils';
import { formatDateTime } from 'utils/dateUtils';

interface ClaimDebitsProps {
  claimID?: string;
}

const formatExternalPaymentAmount = (
  payment: ExternalPaymentLinkSummary,
  locale: string
): string | null => {
  if (!payment.amount || !payment.currency) return null;

  const amount = Number(payment.amount);
  if (!Number.isFinite(amount)) return null;

  try {
    return Intl.NumberFormat(locale, {
      style: 'currency',
      currency: payment.currency,
    }).format(amount);
  } catch {
    return `${payment.amount} ${payment.currency}`;
  }
};

export const ClaimDebits = ({ claimID }: ClaimDebitsProps) => {
  const { translate, getLocale } = useTranslation();
  const computedColorScheme = useComputedColorScheme('light');
  const { timezone } = useCurrentSiteStore();
  const {
    data: debits = [],
    isLoading: loadingDebits,
    isError: errorDebits,
  } = useGetDebitByClaim({
    claimID: claimID ?? '',
  });
  const {
    data: stripePayments = [],
    isLoading: loadingStripePayments,
    isError: errorStripePayments,
    refetch: refetchStripePayments,
  } = useClaimExternalPaymentLinks(claimID ?? '');
  const locale = getLocale() ?? 'en-CA';

  return (
    <Stack gap="md" w="100%">
      <Text size="sm" c="dimmed">
        {translate(
          'pages:claims.paymentHistory.description',
          'Payment records and adjustments tied to this claim.'
        )}
      </Text>

      {loadingDebits ? (
        <Center h={180}>
          <Stack gap="xs" align="center">
            <Loader />
            <Text size="sm" c="dimmed">
              {translate(
                'pages:claims.paymentHistory.loading',
                'Loading payment history...'
              )}
            </Text>
          </Stack>
        </Center>
      ) : errorDebits ? (
        <Paper withBorder radius="md" p="lg">
          <Text c="red" fw={600}>
            {translate(
              'pages:claims.paymentHistory.loadErrorTitle',
              'Unable to load payment history.'
            )}
          </Text>
          <Text size="sm" c="dimmed">
            {translate(
              'pages:claims.paymentHistory.loadErrorMessage',
              'Please try again or refresh the claim.'
            )}
          </Text>
        </Paper>
      ) : debits?.length ? (
        <Paper withBorder radius="md" p="md">
          <DebitTimeline
            data={debits}
            isError={errorDebits}
            isLoading={loadingDebits}
          />
        </Paper>
      ) : (
        <Paper withBorder radius="md" p="lg">
          <Title order={4}>
            {translate(
              'pages:claims.paymentHistory.emptyTitle',
              'No Payment History Found'
            )}
          </Title>
          <Text size="sm" c="dimmed" mt="xs">
            {translate(
              'pages:claims.paymentHistory.emptyMessage',
              'This claim does not have any recorded payments yet.'
            )}
          </Text>
        </Paper>
      )}

      {loadingStripePayments && (
        <Group gap="xs">
          <Loader size="sm" />
          <Text size="sm" c="dimmed">
            {translate(
              'pages:claims.paymentHistory.stripeLoading',
              'Loading Stripe payments...'
            )}
          </Text>
        </Group>
      )}

      {errorStripePayments && (
        <Alert
          color="yellow"
          title={translate(
            'pages:claims.paymentHistory.stripeLoadErrorTitle',
            'Stripe links unavailable'
          )}
        >
          <Group justify="space-between" align="center" wrap="wrap">
            <Text size="sm">
              {translate(
                'pages:claims.paymentHistory.stripeLoadErrorMessage',
                'Payment history is still available. Try loading the Stripe links again.'
              )}
            </Text>
            <Button
              size="xs"
              variant="light"
              onClick={() => void refetchStripePayments()}
            >
              {translate('pages:claims.paymentHistory.retry', 'Retry')}
            </Button>
          </Group>
        </Alert>
      )}

      {stripePayments.length > 0 && (
        <Stack gap="xs">
          <Text fw={600}>
            {translate(
              'pages:claims.paymentHistory.stripeTitle',
              'Stripe payments'
            )}
          </Text>
          {stripePayments.map((payment) => {
            const formattedAmount = formatExternalPaymentAmount(
              payment,
              locale
            );
            const formattedDate = formatDateTime(payment.processorCreatedAt, {
              timezone,
              fallback: translate(
                'pages:claims.paymentHistory.unknownPaymentDate',
                'Unknown payment date'
              ),
            });

            return (
              <Paper key={payment.id} withBorder radius="md" p="md">
                <Group justify="space-between" align="center" wrap="wrap">
                  <Stack gap={4}>
                    <Group gap="xs" wrap="wrap">
                      {formattedAmount && (
                        <Text fw={600}>{formattedAmount}</Text>
                      )}
                      <Text size="sm" c="dimmed">
                        {formattedDate}
                      </Text>
                    </Group>
                    <Text size="sm" ff="monospace">
                      {payment.processorPaymentID}
                    </Text>
                  </Stack>
                  <Button
                    component="a"
                    href={payment.dashboardURL}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="sm"
                    leftSection={<IconExternalLink size={16} />}
                    {...getOutlineControlProps(computedColorScheme, false)}
                  >
                    {translate(
                      'pages:claims.paymentHistory.openInStripe',
                      'Open in Stripe'
                    )}
                  </Button>
                </Group>
              </Paper>
            );
          })}
        </Stack>
      )}
    </Stack>
  );
};
